import React, { forwardRef } from 'react';

const LABEL_STYLE = {
    color: '#000000',
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '1.33',
    marginBottom: '6px'
};

const INPUT_STYLE = {
    background: '#fafbfc',
    border: '1px solid #b5cbeb',
    borderRadius: '8px',
    boxSizing: 'border-box',
    color: '#333333',
    fontSize: '14px',
    minHeight: '40px',
    outline: 'none',
    padding: '10px 12px',
    width: '100%'
};

/**
 * Compatibility replacement for Atlaskit text inputs.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.autoFocus - Whether the input should autofocus.
 * @param {string} props.className - Additional class names.
 * @param {boolean} props.compact - Unused compatibility prop.
 * @param {string|number} props.defaultValue - Default value.
 * @param {boolean} props.disabled - Whether the input is disabled.
 * @param {string} props.id - Input id.
 * @param {boolean} props.isInvalid - Whether the input is invalid.
 * @param {React$Node} props.label - Label content.
 * @param {number} props.maxLength - Max length.
 * @param {string} props.name - Input name.
 * @param {Function} props.onBlur - Blur handler.
 * @param {Function} props.onChange - Change handler.
 * @param {Function} props.onFocus - Focus handler.
 * @param {Function} props.onKeyDown - Keydown handler.
 * @param {Function} props.onKeyPress - Keypress handler.
 * @param {string} props.placeholder - Placeholder.
 * @param {boolean} props.readOnly - Whether the input is readonly.
 * @param {boolean} props.required - Whether the input is required.
 * @param {boolean} props.shouldFitContainer - Width hint.
 * @param {string} props.type - Input type.
 * @param {string|number} props.value - Current value.
 * @returns {React$Node}
 */
const TextField = forwardRef(({
    autoFocus,
    className = '',
    compact: _compact,
    defaultValue,
    disabled,
    id,
    isInvalid,
    label,
    maxLength,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onKeyPress,
    placeholder,
    readOnly,
    required,
    shouldFitContainer,
    type = 'text',
    value,
    ...rest
}, ref) => {
    const wrapperStyle = shouldFitContainer ? { width: '100%' } : undefined;
    const mergedStyle = {
        ...INPUT_STYLE,
        ...isInvalid ? { borderColor: '#d83848',
            boxShadow: '0 0 0 1px #d83848' } : null
    };

    return (
        <div style = { wrapperStyle }>
            {label ? <label
                htmlFor = { id }
                style = { LABEL_STYLE }>{label}</label> : null}
            <input
                { ...rest }
                autoFocus = { autoFocus }
                className = { `input-control ${className}`.trim() }
                defaultValue = { defaultValue }
                disabled = { disabled }
                id = { id }
                maxLength = { maxLength }
                name = { name }
                onBlur = { onBlur }
                onChange = { onChange }
                onFocus = { onFocus }
                onKeyDown = { onKeyDown }
                onKeyPress = { onKeyPress }
                placeholder = { placeholder }
                readOnly = { readOnly }
                ref = { ref }
                required = { required }
                style = { mergedStyle }
                type = { type }
                value = { value } />
        </div>
    );
});

export default TextField;
